import { StackScreenProps } from "@react-navigation/stack";
import {
  EditStockOrderRootScreenParamList,
  StockOrderRootParamList,
} from "../../../../types";
import { useStockOrderContextProvider } from "../../utils/active-stock-order";
import {
  Badge,
  Button,
  Card,
  FlatActionList,
  Page,
  Stack,
  StockOrderStatus,
  Text,
  TileButtonActionList,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { Spacings, View } from "react-native-ui-lib";
import React from "react";
import { useTranslation } from "../../../../utilities/i18n";
import {
  MUTATION_CANCEL_STOCK_ORDER,
  MUTATION_COMPLETE_STOCK_ORDER,
} from "../../utils";
import { useMutation } from "@apollo/client";
import { StockOrder } from "@towersystems/roam-common/lib/generated-types";
import { ConfirmationPrompt } from "../../../../components/ConfirmationPrompt";
import { OrderItemFlatActionList } from "../../components/OrderItemFlatActionList/OrderItemFlatActionList";

export type EditStockOrderScreenProps = StackScreenProps<
  EditStockOrderRootScreenParamList & StockOrderRootParamList,
  "EditStockOrderScreen"
>;

export const EditStockOrderScreen = ({
  navigation,
}: EditStockOrderScreenProps) => {
  const { t } = useTranslation("stockorder");
  const [promptVisible, setPromptVisible] = React.useState(false);

  const { stockOrder, setStockOrder } = useStockOrderContextProvider();
  const flashMessagesContext = useFlashMessages();

  const [cancelStockOrderMutation, { loading: cancellingOrder }] = useMutation<{
    cancelStockOrder: StockOrder;
  }>(MUTATION_CANCEL_STOCK_ORDER);

  const [completeStockOrderMutation, { loading: completingOrder }] =
    useMutation<{
      completeStockOrder: StockOrder;
    }>(MUTATION_COMPLETE_STOCK_ORDER);

  const title = `${t("edit_name")} - #${stockOrder.formattedOrderNumber}`;

  const handleOnCompleteFn = React.useCallback(() => {
    return completeStockOrderMutation({
      variables: {
        id: stockOrder.id,
      },
    })
      .then((result) => {
        if (result.data?.completeStockOrder) {
          setStockOrder(result.data.completeStockOrder);
          navigation.reset({
            index: 0,
            routes: [
              { name: "StockOrderListScreen" },
              {
                name: "ViewStockOrderRootScreen",
                params: {
                  stockOrderId: result.data?.completeStockOrder.id,
                },
              },
            ],
          });
        }
      })
      .catch((err) => {
        flashMessagesContext.showMessage({
          message: err.message ?? t("common:error"),
          severity: "error",
        });
      });
  }, []);

  const handleOnCancelFn = React.useCallback(() => {
    return cancelStockOrderMutation({
      variables: {
        id: stockOrder.id,
      },
    })
      .then((result) => {
        if (result.data?.cancelStockOrder) {
          setPromptVisible(false);
          setStockOrder(result.data.cancelStockOrder);
          navigation.goBack();
        }
      })
      .catch((err) => {
        flashMessagesContext.showMessage({
          message: err.message ?? t("common:error"),
          severity: "error",
        });
      });
  }, []);

  let actions = [
    {
      isAction: true,
      id: "complete",
      label: t("action.complete_order.label"),
      disabled: stockOrder.items.length === 0 || completingOrder,
      children: (
        <View style={{ alignItems: "center" }}>
          <Text color="subdued" style={{ textAlign: "center" }}>
            {t("action.complete_order.description")}
          </Text>
        </View>
      ),
      onPress: handleOnCompleteFn,
    },
    {
      isDestructive: true,
      id: "cancel",
      label: t("action.cancel_order.label"),
      disabled: cancellingOrder,
      onPress: () => setPromptVisible(true),
      children: (
        <View style={{ alignItems: "center" }}>
          <Text color="subdued" style={{ textAlign: "center" }}>
            {t("action.cancel_order.description")}
          </Text>
        </View>
      ),
    },
  ];

  const cancelPrompt = (
    <ConfirmationPrompt
      visible={promptVisible}
      title={t("prompt.cancel_order.title")}
      description={t("prompt.cancel_order.description")}
      onCancelPress={() => setPromptVisible(false)}
      onConfirmPress={handleOnCancelFn}
      disabled={cancellingOrder}
    />
  );

  const actionsMarkup =
    actions.length >= 1 ? (
      <View>
        <TileButtonActionList actions={actions} />
      </View>
    ) : null;

  return (
    <Page
      title={title}
      badges={
        <Stack spacing={Spacings.s2}>
          <Badge
            stockOrderStatus={stockOrder.status as StockOrderStatus}
          ></Badge>
        </Stack>
      }
    >
      <Stack vertical>
        {actionsMarkup}
        <Card title={t("card.details.title")}>
          <FlatActionList
            items={[
              {
                key: "order_number",
                title: t("form.order_number.label"),
                subTitle: t("form.order_number.description"),
                componentRight: () => (
                  <Text fontWeight="bold">{`#${stockOrder.formattedOrderNumber}`}</Text>
                ),
              },
              {
                key: "location",
                title: t("form.location.label"),
                subTitle: t("form.location.description"),
                componentRight: () => (
                  <Text fontWeight="bold">
                    {stockOrder.location?.name ?? "-"}
                  </Text>
                ),
              },
            ]}
          />
        </Card>
        <Card title={t("card.product.title")}>
          <Stack vertical>
            <Button
              isAction
              label={t("button.add_order_item")}
              onPress={() =>
                navigation.navigate("AddOrderItemModalRootScreen", {
                  screen: "SearchSelectProductScreen",
                })
              }
            ></Button>
            <OrderItemFlatActionList
              items={stockOrder.items}
              onPressAll={(item) => {
                navigation.navigate("EditOrderItemModalRootScreen", {
                  screen: "AddOrEditOrderItemScreen",
                  lineItemId: item.id,
                });
              }}
            />
          </Stack>
        </Card>
      </Stack>
      {cancelPrompt}
      <View style={{ height: 0, marginBottom: Spacings.s7 * 3 }} />
    </Page>
  );
};
